import styled from "@emotion/styled";
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Spin,
  Select,
  Switch,
  Result,
  Modal,
  Alert,
} from "antd";
import { useCompany } from "../hooks/useCompany";
import { useCompanyDetails } from "../hooks/useCompanyDetails";
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useTranslation } from "react-i18next";
import { WmsConnectionStatus } from "../components/WmsConnectionStatus";

const CompanyInfoContainer = styled.div`
  padding: 20px;
`;

const StyledCard = styled(Card)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const StyledForm = styled(Form)`
  max-width: 600px;

  .ant-form-item {
    margin-bottom: 24px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const countries = [
  { value: "NL", label: "Nederland" },
  { value: "BE", label: "België" },
];

interface CompanyForm {
  name: string;
  status: string;
  country: string;
  city: string;
  postal_code: string;
  vat_number: string;
  eori_number: string;
  coc_number: number;
  article_23: boolean;
  phone_number: string;
  street: string | null;
  house_number: string | null;
}

// Contact info component to ensure consistent rendering
const ContactInfo = () => {
  const { t } = useTranslation();
  const email = "info@efreightforwarding.com";
  const phone = "+31 (0)85 0607 063";

  // Split the translation string into parts
  const parts = t("companyInfo.contactInfo", {
    email: "{email}",
    phone: "{phone}",
  }).split(/\{(email|phone)\}/);

  return (
    <span>
      {parts.map((part, index) => {
        if (part === "email") {
          return (
            <a key={index} href={`mailto:${email}`}>
              {email}
            </a>
          );
        } else if (part === "phone") {
          return (
            <a key={index} href={`tel:${phone.replace(/\s/g, "")}`}>
              {phone}
            </a>
          );
        } else {
          return <span key={index}>{part}</span>;
        }
      })}
    </span>
  );
};

export const CompanyInfo = () => {
  const { t } = useTranslation();
  const { selectedCompany, setSelectedCompany } = useCompany();
  const { details, loading, updateCompany } =
    useCompanyDetails(selectedCompany);
  const [form] = Form.useForm();
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingValues, setPendingValues] = useState<CompanyForm | null>(null);

  // Reset form when company or details change
  useEffect(() => {
    form.setFieldsValue(details || {});
  }, [form, details, selectedCompany]);

  const handleSubmit = async (values: CompanyForm) => {
    // Check if critical fields (VAT, EORI, or CoC) have changed
    const criticalFieldsChanged =
      details?.vat_number !== values.vat_number ||
      details?.eori_number !== values.eori_number ||
      details?.coc_number !== values.coc_number;

    // If company status is approved and critical fields changed, show confirmation modal first
    if (details?.status === "approved" && criticalFieldsChanged) {
      setPendingValues(values);
      setShowConfirmationModal(true);
      return;
    }

    // Otherwise proceed with the update
    await submitUpdate(values);
  };

  const submitUpdate = async (values: CompanyForm) => {
    // Check if critical fields (VAT, EORI, or CoC) have changed
    const criticalFieldsChanged =
      details?.vat_number !== values.vat_number ||
      details?.eori_number !== values.eori_number ||
      details?.coc_number !== values.coc_number;

    // Only set status to "pending" if critical fields changed
    const updatedValues = {
      ...values,
      status: criticalFieldsChanged
        ? "pending"
        : details?.status || values.status,
    };

    const success = await updateCompany(updatedValues);
    if (success) {
      // Show verification modal only if critical fields changed
      if (criticalFieldsChanged) {
        setShowVerificationModal(true);
      } else {
        message.success(t("companyInfo.updateSuccess"));
      }
    } else {
      message.error(t("companyInfo.updateError"));
    }
  };

  const handleConfirmUpdate = () => {
    if (pendingValues) {
      submitUpdate(pendingValues);
      setPendingValues(null);
    }
    setShowConfirmationModal(false);
  };

  const handleCancelUpdate = () => {
    setPendingValues(null);
    setShowConfirmationModal(false);
  };

  // Function to create a new company for the current user
  const createCompany = async () => {
    try {
      setCreatingCompany(true);

      // Get the current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        message.error(t("companyInfo.loginRequired"));
        setCreatingCompany(false);
        return;
      }

      // Create the company and get the ID in a single operation
      const { data: companyData, error: companyError } = await supabase
        .from("company")
        .insert({
          name: "",
          status: "new",
          created_by_user_id: user.id,
          street: "",
          house_number: "",
        })
        .select(); // This will try to return the created record

      if (companyError) {
        console.error("Error creating company:", companyError);
        message.error(t("companyInfo.createError"));
        setCreatingCompany(false);
        return;
      }

      if (!companyData || companyData.length === 0) {
        // If we can't get the ID due to RLS, try a workaround
        console.log(
          "Company created but couldn't fetch ID due to RLS. Trying workaround..."
        );

        // Get all companies created by this user
        const { data: userCompanies, error: fetchError } = await supabase
          .from("company")
          .select("id")
          .eq("created_by_user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1);

        if (fetchError || !userCompanies || userCompanies.length === 0) {
          console.error("Failed to get company ID:", fetchError);
          message.error(t("companyInfo.idRetrievalError"));
          setCreatingCompany(false);
          return;
        }

        // Use the most recently created company
        const companyId = userCompanies[0].id;

        // Link user to company
        const { error: linkError } = await supabase
          .from("user_companies")
          .insert({
            company_id: companyId,
            user_id: user.id,
          });

        if (linkError) {
          console.error("Error linking user to company:", linkError);
          message.error(t("companyInfo.linkError"));
          setCreatingCompany(false);
          return;
        }

        // Set as selected company
        localStorage.setItem("selectedCompany", companyId.toString());
        setSelectedCompany(companyId);
      } else {
        // We got the company data with ID
        const companyId = companyData[0].id;

        // Link user to company
        const { error: linkError } = await supabase
          .from("user_companies")
          .insert({
            company_id: companyId,
            user_id: user.id,
          });

        if (linkError) {
          console.error("Error linking user to company:", linkError);
          message.error(t("companyInfo.linkError"));
          setCreatingCompany(false);
          return;
        }

        // Set as selected company
        localStorage.setItem("selectedCompany", companyId.toString());
        setSelectedCompany(companyId);
      }

      message.success(t("companyInfo.createSuccess"));

      // Reload the page to show the company information form
      window.location.reload();
    } catch (error) {
      console.error("Unexpected error:", error);
      message.error(t("common.unexpectedError"));
    } finally {
      setCreatingCompany(false);
    }
  };

  // Function to handle modal close
  const handleModalClose = () => {
    setShowVerificationModal(false);
  };

  // Check if company status is pending
  const isPending = details?.status === "pending";

  if (loading) {
    return (
      <CompanyInfoContainer>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      </CompanyInfoContainer>
    );
  }

  // If no company is selected or no details are available, show the create company button
  if (!selectedCompany || !details) {
    return (
      <CompanyInfoContainer>
        <h1>{t("common.companyInfo")}</h1>
        <StyledCard>
          <Result
            status="info"
            title={t("companyInfo.noCompanyFound")}
            subTitle={t("companyInfo.createCompanyPrompt")}
            extra={
              <Button
                type="primary"
                onClick={createCompany}
                loading={creatingCompany}
                size="large"
              >
                {t("companyInfo.createCompany")}
              </Button>
            }
          />
        </StyledCard>
      </CompanyInfoContainer>
    );
  }

  return (
    <CompanyInfoContainer>
      <h1>{t("common.companyInfo")}</h1>

      {isPending && (
        <Alert
          type="warning"
          message={
            <span>
              {t("companyInfo.verificationInProgress")}
              <br />
              <br />
              <ContactInfo />
            </span>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <StyledCard>
        <StyledForm
          form={form}
          layout="vertical"
          initialValues={details || {}}
          onFinish={(values) => handleSubmit(values as CompanyForm)}
          disabled={isPending}
        >
          <FormRow>
            <Form.Item
              label={t("companyInfo.companyName")}
              name="name"
              rules={[
                {
                  required: true,
                  message: t("companyInfo.companyNameRequired"),
                },
              ]}
            >
              <Input placeholder={t("companyInfo.companyNamePlaceholder")} />
            </Form.Item>

            <Form.Item
              label={t("companyInfo.vatNumber")}
              name="vat_number"
              rules={[
                { required: true, message: t("companyInfo.vatNumberRequired") },
              ]}
            >
              <Input placeholder={t("companyInfo.vatNumberPlaceholder")} />
            </Form.Item>
          </FormRow>

          <FormRow>
            <Form.Item
              label={t("companyInfo.city")}
              name="city"
              rules={[
                { required: true, message: t("companyInfo.cityRequired") },
              ]}
            >
              <Input placeholder={t("companyInfo.cityPlaceholder")} />
            </Form.Item>

            <Form.Item
              label={t("companyInfo.postalCode")}
              name="postal_code"
              rules={[
                {
                  required: true,
                  message: t("companyInfo.postalCodeRequired"),
                },
              ]}
            >
              <Input placeholder={t("companyInfo.postalCodePlaceholder")} />
            </Form.Item>
          </FormRow>

          <FormRow>
            <Form.Item
              label={t("companyInfo.street")}
              name="street"
              rules={[
                { required: true, message: t("companyInfo.streetRequired") },
              ]}
            >
              <Input placeholder={t("companyInfo.streetPlaceholder")} />
            </Form.Item>

            <Form.Item
              label={t("companyInfo.houseNumber")}
              name="house_number"
              rules={[
                {
                  required: true,
                  message: t("companyInfo.houseNumberRequired"),
                },
              ]}
            >
              <Input placeholder={t("companyInfo.houseNumberPlaceholder")} />
            </Form.Item>
          </FormRow>

          <FormRow>
            <Form.Item
              label={t("companyInfo.country")}
              name="country"
              rules={[
                { required: true, message: t("companyInfo.countryRequired") },
              ]}
            >
              <Select
                placeholder={t("companyInfo.countryPlaceholder")}
                options={countries}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item
              label={t("companyInfo.phoneNumber")}
              name="phone_number"
              rules={[
                {
                  required: true,
                  message: t("companyInfo.phoneNumberRequired"),
                },
                {
                  pattern: /^\+?[\d\s-]+$/,
                  message: t("companyInfo.phoneNumberInvalid"),
                },
              ]}
            >
              <Input placeholder={t("companyInfo.phoneNumberPlaceholder")} />
            </Form.Item>
          </FormRow>

          <FormRow>
            <Form.Item
              label={t("companyInfo.eoriNumber")}
              name="eori_number"
              rules={[
                {
                  required: true,
                  message: t("companyInfo.eoriNumberRequired"),
                },
              ]}
            >
              <Input placeholder={t("companyInfo.eoriNumberPlaceholder")} />
            </Form.Item>

            <Form.Item
              label={t("companyInfo.cocNumber")}
              name="coc_number"
              rules={[
                { required: true, message: t("companyInfo.cocNumberRequired") },
                {
                  pattern: /^\d+$/,
                  message: t("companyInfo.cocNumberInvalid"),
                },
              ]}
            >
              <Input placeholder={t("companyInfo.cocNumberPlaceholder")} />
            </Form.Item>
          </FormRow>

          <Form.Item
            label={t("companyInfo.article23")}
            name="article_23"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" disabled={isPending}>
              {t("companyInfo.saveChanges")}
            </Button>
          </Form.Item>
        </StyledForm>
      </StyledCard>

      {/* WMS Connection Status (Picqer or ChannelDock) */}
      <WmsConnectionStatus details={details} />

      {/* Verification Modal */}
      <Modal
        open={showVerificationModal}
        onCancel={handleModalClose}
        footer={[
          <Button key="ok" type="primary" onClick={handleModalClose}>
            {t("common.ok")}
          </Button>,
        ]}
        title={t("companyInfo.verificationTitle")}
        centered
      >
        <Result
          status="success"
          title={t("companyInfo.informationSubmitted")}
          subTitle={
            <>
              {t("companyInfo.verificationMessage")}
              <br />
              <br />
              <ContactInfo />
            </>
          }
        />
      </Modal>

      {/* Confirmation Modal for approved companies */}
      <Modal
        open={showConfirmationModal}
        onCancel={handleCancelUpdate}
        footer={[
          <Button key="cancel" onClick={handleCancelUpdate}>
            {t("common.cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            onClick={handleConfirmUpdate}
          >
            {t("common.confirm")}
          </Button>,
        ]}
        title={t("companyInfo.confirmUpdateTitle") || "Confirmation Required"}
        centered
      >
        <p>
          {t("companyInfo.confirmUpdateMessage") ||
            "If you submit this, we will have to manually approve your company again. You will not be able to make new bookings in the meanwhile."}
        </p>
      </Modal>
    </CompanyInfoContainer>
  );
};
